import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Smartphone, Home } from "lucide-react";
import { useState, useEffect } from "react";
import AnimatedLogo from "./AnimatedLogo";
import ThemeToggle from "./ThemeToggle";
import UserMenu from "./UserMenu";

export function BackButton({ fallbackPath = "/dashboard", className = "" }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    // Check if we have history to go back to
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate(fallbackPath);
    }
  };

  // Don't show on main pages
  const mainPages = ["/", "/dashboard", "/admin", "/play", "/login", "/register"];
  if (mainPages.includes(location.pathname)) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleBack}
      className={`gap-2 text-muted-foreground hover:text-foreground ${className}`}
      data-testid="back-button"
    >
      <ArrowLeft className="w-4 h-4" />
      <span className="hidden sm:inline">Retour</span>
    </Button>
  );
}

export function InstallAppButton({ variant = "compact" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Listen for beforeinstallprompt event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // For iOS/Safari that doesn't fire beforeinstallprompt
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile && !isInstalled) {
      setCanInstall(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setCanInstall(false);
      }
      setDeferredPrompt(null);
    } else {
      // iOS/Safari - show instructions
      const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
      if (isIOS) {
        alert(
          "Pour installer TIREO sur votre iPhone/iPad :\n\n" +
          "1. Appuyez sur le bouton de partage (carré avec flèche)\n" +
          "2. Faites défiler et appuyez sur 'Sur l'écran d'accueil'\n" +
          "3. Appuyez sur 'Ajouter'"
        );
      } else {
        alert(
          "Pour installer TIREO sur votre appareil :\n\n" +
          "Appuyez sur le menu (⋮) en haut à droite,\npuis 'Installer l'application' ou 'Ajouter à l'écran d'accueil'"
        );
      }
    }
  };

  if (isInstalled || !canInstall) return null;

  if (variant === "icon") {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={handleInstall}
        className="text-muted-foreground hover:text-foreground"
        title="Installer l'application"
        data-testid="install-app-btn"
      >
        <Download className="w-5 h-5" />
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleInstall}
      className="gap-2"
      data-testid="install-app-btn"
    >
      <Download className="w-4 h-4" />
      <span className="hidden sm:inline">Installer</span>
    </Button>
  );
}

export default function AppHeader({ 
  showBack = true, 
  showInstall = true,
  showTheme = true,
  showUser = true,
  userVariant = "default",
  fallbackPath = "/dashboard",
  title,
  rightContent,
  className = ""
}) {
  return (
    <header className={`bg-card border-b border-border sticky top-0 z-50 ${className}`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-2">
        {/* Left side - Back button + Logo */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {showBack && <BackButton fallbackPath={fallbackPath} />}
          <AnimatedLogo linkTo="/dashboard" showAnimation={true} size="small" className="sm:hidden" />
          <AnimatedLogo linkTo="/dashboard" showAnimation={true} size="default" className="hidden sm:flex" />
          {title && (
            <span className="hidden md:inline text-lg font-semibold text-foreground ml-4">
              {title}
            </span>
          )}
        </div>
        
        {/* Right side */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {rightContent}
          {showInstall && <InstallAppButton variant="icon" />}
          {showTheme && <ThemeToggle />}
          {showUser && <UserMenu variant={userVariant} />}
        </div>
      </div>
    </header>
  );
}
